import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { toBigInt } from '@morpho-org/dapp-core'
import { formatters } from '@morpho-org/ui-v6/utils'
import { Alert, WarningTriangle } from '@repo/ui/icons'
import { createElement, type ReactElement } from 'react'

import type { Warning } from '@/components/common/WarningIcon'
import type { MarketWarningFields, VaultV2WarningFields, VaultWarningFields } from '@/types/fields'

import {
  type ApiVaultWarning,
  type ApiWarningLevel,
  type VersionedWarnings,
  warningLevelSchema
} from '@/helpers/warningTypes'
import { VaultV2WarningLevel, WarningLevel } from '@/types/enums'

// Stable string for the V2 vault `version` discriminator, checked against the
// vault's `version` to pick its own-warning `source`.
const VAULT_V2_VERSION = '2.0'

export enum MarketWarningType {
  CUSTOM = 'custom',
  BAD_DEBT_UNREALIZED = 'bad_debt_unrealized',
  NOT_WHITELISTED = 'not_whitelisted',
  // The Chainlink(-V2) price deviates by more than 10x from the USD-feed-derived one. Shares
  // its string value with the two vault enums below, as `ORACLE_UNUSABLE` does.
  ORACLE_PRICE_DERIVATION = 'oracle_price_derivation',
  // The oracle either reverts when queried or returns a zero price (`metadata.type` is
  // `'reverted'` | `'zero_price'`). Shares its string value with the two vault enums below,
  // so the three are told apart by source, not by value.
  ORACLE_UNUSABLE = 'oracle_unusable',
  SUSTAINED_LOW_LIQUIDITY = 'sustained_low_liquidity'
}

export enum VaultWarningType {
  CUSTOM = 'custom',
  SHORT_TIMELOCK = 'short_timelock',
  NOT_WHITELISTED = 'not_whitelisted',
  DEPOSIT_DISABLED = 'deposit_disabled',
  LOW_LIQUIDITY = 'low_liquidity',
  // See `MarketWarningType.ORACLE_PRICE_DERIVATION`.
  ORACLE_PRICE_DERIVATION = 'oracle_price_derivation',
  // See `MarketWarningType.ORACLE_UNUSABLE`.
  ORACLE_UNUSABLE = 'oracle_unusable'
}

export enum VaultV2WarningType {
  CUSTOM = 'custom',
  NOT_WHITELISTED = 'not_whitelisted',
  TIMELOCK = 'timelock',
  DEPOSIT_DISABLED = 'deposit_disabled',
  LOW_LIQUIDITY = 'low_liquidity',
  // See `MarketWarningType.ORACLE_PRICE_DERIVATION`.
  ORACLE_PRICE_DERIVATION = 'oracle_price_derivation',
  // See `MarketWarningType.ORACLE_UNUSABLE`.
  ORACLE_UNUSABLE = 'oracle_unusable',
  // V2-only. Same string value as `MarketWarningType.BAD_DEBT_UNREALIZED`, so the two are
  // told apart by source, not by value.
  BAD_DEBT_UNREALIZED = 'bad_debt_unrealized'
}

/**
 * The API emits `oracle_unusable` as a market warning **and**, gated on exposure, as its own
 * vault warning — two independent warnings that need different copy.
 *
 * Both state the risk only. The deposit consequence is deliberately left to the disabled
 * deposit button and its tooltip (see {@link isDepositDisabledByWarning}), the same split
 * `deposit_disabled` already uses: the block is RED-only and V2-parent rows can carry an
 * adapter-sourced copy of this warning, so copy that asserted "deposits are disabled" would
 * contradict the controls on those surfaces.
 */
export const ORACLE_UNUSABLE_MARKET_MESSAGE =
  "This market's oracle is failing to return a price. Positions here can't be valued reliably, so liquidations and health factors may be inaccurate."

export const ORACLE_UNUSABLE_VAULT_MESSAGE =
  "This vault is allocating into a market whose oracle is failing to return a price. That market's share of the vault can't be valued reliably."

/**
 * V2-only: V1 vaults get no bad-debt vault warning from the API. Informational — unlike
 * `oracle_unusable`, it does not block deposits. The per-market amounts stay on the market
 * surfaces; the vault warning names the exposure, not a number.
 */
export const BAD_DEBT_UNREALIZED_VAULT_MESSAGE =
  'This vault is allocating into a market with unrealized bad debt.'

export const ORACLE_PRICE_DERIVATION_MARKET_MESSAGE = "The Market's Oracle is misconfigured"

// Informational: unlike `oracle_unusable`, this one does not block deposits.
export const ORACLE_PRICE_DERIVATION_VAULT_MESSAGE =
  'This vault is allocating into a market with a misconfigured oracle. Collateral there may be mispriced, so liquidations can fire at the wrong time and leave bad debt for lenders.'

export const SUSTAINED_LOW_LIQUIDITY_MARKET_MESSAGE =
  "This market has had zero available liquidity for at least five days. Until liquidity returns, lenders can't withdraw and no new borrows are possible."

/**
 * `WARNING_SCROLL_CONFIG` is keyed by the bare type string, and these two share theirs with
 * `MarketWarningType`, whose anchors only market pages render. Vault-sourced warnings pass
 * their type through here; market-sourced ones keep the target.
 */
export function getVaultWarningScrollType(warningType: string): string | undefined {
  return warningType === MarketWarningType.BAD_DEBT_UNREALIZED ||
    warningType === MarketWarningType.ORACLE_PRICE_DERIVATION
    ? undefined
    : warningType
}

export declare type UnifiedVaultWarningLevel = VaultV2WarningLevel | WarningLevel

export interface UnifiedVaultWarning {
  level: UnifiedVaultWarningLevel
  type: string
}

// Not `Omit<ApiVaultWarning, 'level'>`: `vaultWarningSchema` uses `.passthrough()`,
// and `Omit` on a type with an index signature collapses named fields to `unknown`.
export type ApiVaultWarningWithEnumLevel = {
  level: UnifiedVaultWarningLevel
  type: ApiVaultWarning['type']
  source?: ApiVaultWarning['source']
  metadata?: ApiVaultWarning['metadata']
  /** Whether a `not_whitelisted` vault was listed before, for sources that send no listing history. */
  deprecated?: boolean
}

/**
 * Warning types that block deposits, worst-first. `oracle_unusable` joins `deposit_disabled`
 * because a vault whose allocated market can't be priced can't mint shares at a trustworthy
 * rate (VAU-1139); withdrawals stay open either way.
 */
const DEPOSIT_BLOCKING_WARNING_TYPES = [
  VaultWarningType.ORACLE_UNUSABLE,
  VaultWarningType.DEPOSIT_DISABLED
] as const

export type DepositDisabledReason = (typeof DEPOSIT_BLOCKING_WARNING_TYPES)[number]

/**
 * Which red warning is blocking deposits, or `undefined` if none is.
 *
 * Callers that only need the on/off state use {@link isDepositDisabledByWarning}; the reason
 * exists because surfaces without a vault warning card (the position-manager deposit dialogs)
 * have nowhere else to explain the block.
 *
 * Works with both V1 and V2 vault warning arrays.
 */
export function getDepositDisabledReason(
  warnings: Array<{ type: string; level: string }> | undefined
): DepositDisabledReason | undefined {
  return DEPOSIT_BLOCKING_WARNING_TYPES.find(type =>
    warnings?.some(w => w.type === type && w.level === 'RED')
  )
}

/**
 * Check if a vault has a red deposit-blocking warning.
 * Works with both V1 and V2 vault warning arrays.
 */
export function isDepositDisabledByWarning(
  warnings: Array<{ type: string; level: string }> | undefined
): boolean {
  return getDepositDisabledReason(warnings) !== undefined
}

/**
 * Consumer-API rows are pre-aggregated across sources: keep only the ones matching the
 * vault's own version, so a market- or adapter-sourced copy never speaks for the parent
 * vault. The GraphQL surfaces get this for free — they only ever see the vault's own list.
 *
 * Native-only rows carry no `source` (#5237); only scope when the BFF still sends it.
 */
export function getOwnApiVaultWarnings(vault: VersionedWarnings): ApiVaultWarning[] {
  const ownSource = vault.version === VAULT_V2_VERSION ? 'vaultV2' : 'vaultV1'
  return (vault.warnings ?? []).filter(w => w.source === undefined || w.source === ownSource)
}

/** Why deposits are blocked, in prose — shown wherever the block needs explaining. */
export const DEPOSIT_DISABLED_MESSAGE: Record<DepositDisabledReason, string> = {
  deposit_disabled: 'Deposits are currently disabled on this vault.',
  oracle_unusable:
    "Deposits are currently disabled: this vault is allocating into a market whose oracle is failing to return a price, so the vault's share price can't be valued reliably."
}

const WARNING_LEVELS: readonly string[] = ['red', 'yellow', 'green'] satisfies ApiWarningLevel[]

export function isApiWarningLevel(level: string): level is ApiWarningLevel {
  return WARNING_LEVELS.includes(level)
}

/** A procedure's warning level as the enum the warning UI renders; null on a level it has none for. */
export function toWarningLevelEnum(level: string): UnifiedVaultWarningLevel | null {
  const parsed = warningLevelSchema.safeParse(level)
  return parsed.success ? apiWarningLevelToEnum(parsed.data) : null
}

export function apiWarningLevelToEnum(level: ApiWarningLevel): UnifiedVaultWarningLevel {
  switch (level) {
    case 'red':
      return WarningLevel.Red
    case 'yellow':
      return WarningLevel.Yellow
    case 'green':
      return VaultV2WarningLevel.Green
  }
}

export function getSeverityRank(level: UnifiedVaultWarningLevel): number {
  switch (level) {
    case WarningLevel.Red:
    case VaultV2WarningLevel.Red:
      return 2
    case WarningLevel.Yellow:
    case VaultV2WarningLevel.Yellow:
      return 1
    case VaultV2WarningLevel.Green:
    default:
      return 0
  }
}

/**
 * Sort warnings by level.
 *
 * @param warnings List of warnings
 * @returns Sorted list of warnings
 */
export function sortWarningsByLevel<T extends { level: UnifiedVaultWarningLevel }>(
  warnings: Array<T>,
  order: 'asc' | 'desc' = 'asc'
) {
  // oxlint-disable-next-line unicorn/no-array-sort -- intentional in-place mutation of caller's array
  return warnings.sort((a, b) => {
    return (getSeverityRank(a.level) - getSeverityRank(b.level)) * (order === 'asc' ? 1 : -1)
  })
}

/**
 * Get worst warning level in a list of warnings.
 * Returns null if there are no warnings or only green warnings.
 *
 * @param warnings List of warnings
 * @returns Worst warning level (actual level from the warnings, not normalized)
 */
function getWorstWarningLevel(
  warnings: Array<{ level: UnifiedVaultWarningLevel }>
): UnifiedVaultWarningLevel | null {
  if (warnings.length === 0) {
    return null
  }

  // Find the warning with the highest severity rank
  const worstWarning = warnings.reduce((worst, current) => {
    const worstRank = getSeverityRank(worst.level)
    const currentRank = getSeverityRank(current.level)
    return currentRank > worstRank ? current : worst
  })

  // Only return non-green warnings (severity rank > 0)
  const worstRank = getSeverityRank(worstWarning.level)
  return worstRank > 0 ? worstWarning.level : null
}

export function getWarningLevelColor(level: UnifiedVaultWarningLevel) {
  switch (level) {
    case 'RED':
      return 'text.interactive.error' as const
    case 'YELLOW':
      return 'text.interactive.warning' as const
  }
}

/**
 * Per-severity presentational style for the warnings hover card — text color,
 * background tint, and icon travel together so they can't drift apart. Error
 * keeps the circle {@link Alert} icon ("stays as it is"); warnings use
 * {@link WarningTriangle} so the two read differently even when their colors are
 * close (light mode). Levels without an entry (e.g. GREEN) fall back to neutral
 * styling at the call site.
 */
const WARNING_LEVEL_STYLE: Partial<
  Record<string, { colorClass: string; bgClass: string; icon: ReactElement }>
> = {
  RED: {
    colorClass: 'text-destructive-foreground',
    bgClass: 'bg-destructive',
    icon: createElement(Alert)
  },
  YELLOW: {
    colorClass: 'text-warning-foreground',
    bgClass: 'bg-warning',
    icon: createElement(WarningTriangle)
  }
}

/**
 * Tailwind text-color class for a warning level (V6 text rendering); `undefined`
 * for levels without a tint (e.g. GREEN).
 */
export function getWarningLevelColorClass(level: UnifiedVaultWarningLevel) {
  return WARNING_LEVEL_STYLE[level]?.colorClass
}

/**
 * Alert-banner background class for a warning level, tinting each warning item by
 * severity instead of a flat neutral grey; `undefined` for levels without a tint.
 */
export function getWarningLevelBgClass(level: UnifiedVaultWarningLevel) {
  return WARNING_LEVEL_STYLE[level]?.bgClass
}

/**
 * Severity icon element for a warning level (defaults to the circle {@link Alert}).
 */
export function getWarningLevelIcon(level: UnifiedVaultWarningLevel): ReactElement {
  return WARNING_LEVEL_STYLE[level]?.icon ?? createElement(Alert)
}

export function getWarningsColor(warnings: Warning[]) {
  const worstLevel = getWorstWarningLevel(warnings)
  if (!worstLevel) return

  return getWarningLevelColor(worstLevel)
}

/**
 * Severity icon element for the worst warning in a list — the trigger icon
 * mirrors the worst item so its shape signals severity before the popover opens.
 */
export function getWarningsIcon(warnings: Warning[]): ReactElement {
  const worstLevel = getWorstWarningLevel(warnings)
  return worstLevel ? getWarningLevelIcon(worstLevel) : createElement(Alert)
}

/**
 * Tailwind text-color class equivalent of {@link getWarningsColor} for
 * coloring V6 icons/text (the `text.*` token isn't a valid CSS color value).
 */
export function getWarningsColorClass(warnings: Warning[]) {
  const worstLevel = getWorstWarningLevel(warnings)
  if (!worstLevel) return

  return getWarningLevelColorClass(worstLevel)
}

export function getMarketWarningMessage({
  warning,
  loanAsset
}: {
  warning: MarketWarningFields
  loanAsset: { symbol?: string; decimals: number }
}) {
  if (
    warning.type === MarketWarningType.BAD_DEBT_UNREALIZED &&
    warning.metadata?.__typename === 'BadDebtUnrealizedMarketWarningMetadata'
  ) {
    const badDebtUnrealizedInUsd = warning.metadata.badDebtUsd
    const badDebtUnrealized = warning.metadata.badDebtAssets
    const formattedBadDebt = formatters.short
      .unit(loanAsset.symbol)
      .digits(2)
      .of(toBigInt(badDebtUnrealized ?? 0n), loanAsset.decimals)
    const formattedBadDebtInUsd = formatters.short
      .unit('$')
      .digits(2)
      .of(safeParseNumber(badDebtUnrealizedInUsd ?? 0), 18)
    return `Unrealized bad debt: ${formattedBadDebt} (${formattedBadDebtInUsd})`
  }

  if (warning.type === MarketWarningType.NOT_WHITELISTED) {
    return 'Market is not listed'
  }

  if (warning.type === MarketWarningType.ORACLE_PRICE_DERIVATION) {
    return ORACLE_PRICE_DERIVATION_MARKET_MESSAGE
  }

  if (warning.type === MarketWarningType.ORACLE_UNUSABLE) {
    return ORACLE_UNUSABLE_MARKET_MESSAGE
  }

  if (warning.type === MarketWarningType.SUSTAINED_LOW_LIQUIDITY) {
    return SUSTAINED_LOW_LIQUIDITY_MARKET_MESSAGE
  }

  if (
    warning.type === MarketWarningType.CUSTOM &&
    warning.metadata?.__typename === 'CustomMetadata' &&
    warning.metadata.content
  ) {
    return warning.metadata.content
  }
}

export function getVaultWarningMessage(warning: VaultWarningFields) {
  if (warning.type === VaultWarningType.SHORT_TIMELOCK) {
    return 'This vault has a timelock below 3 days'
  }

  if (warning.type === VaultWarningType.NOT_WHITELISTED) {
    if (
      warning.metadata?.__typename === 'NotWhitelistedVaultWarningMetadata' &&
      warning.metadata.history.length > 0
    ) {
      return 'This vault is deprecated.'
    }
    return 'Vault is not listed'
  }

  if (warning.type === VaultWarningType.LOW_LIQUIDITY) {
    return "Less than 0.5% of this vault's deposits are currently available to withdraw. You may not be able to exit your full position immediately."
  }

  if (warning.type === VaultWarningType.ORACLE_PRICE_DERIVATION) {
    return ORACLE_PRICE_DERIVATION_VAULT_MESSAGE
  }

  if (warning.type === VaultWarningType.ORACLE_UNUSABLE) {
    return ORACLE_UNUSABLE_VAULT_MESSAGE
  }

  // `deposit_disabled` is vault state, not a risk warning, so it produces no warning
  // message. It is surfaced via the disabled deposit button + tooltip instead.

  if (
    warning.type === VaultWarningType.CUSTOM &&
    warning.metadata?.__typename === 'CustomMetadata' &&
    warning.metadata.content
  ) {
    return warning.metadata.content
  }
}

const hasMetadataHistory = (metadata: unknown): boolean => {
  return (
    typeof metadata === 'object' &&
    metadata !== null &&
    'history' in metadata &&
    Array.isArray(metadata.history) &&
    metadata.history.length > 0
  )
}

const getMetadataContent = (metadata: unknown): string | null => {
  if (typeof metadata !== 'object' || metadata === null || !('content' in metadata)) return null

  const content = metadata.content
  return typeof content === 'string' ? content : null
}

export function getVaultV2WarningMessage(
  warning: UnifiedVaultWarning | VaultV2WarningFields | ApiVaultWarningWithEnumLevel
) {
  if (warning.type === VaultV2WarningType.NOT_WHITELISTED) {
    if (
      ('deprecated' in warning && warning.deprecated) ||
      ('metadata' in warning && hasMetadataHistory(warning.metadata))
    ) {
      return 'This vault is deprecated.'
    }
    return 'Vault is not listed'
  }

  if (warning.type === VaultV2WarningType.TIMELOCK) {
    return "Some of the vault's timelocks are set below the minimum required duration"
  }

  if (warning.type === VaultV2WarningType.LOW_LIQUIDITY) {
    return "Less than 0.5% of this vault's deposits are currently available to withdraw. You may not be able to exit your full position immediately."
  }

  if (warning.type === VaultV2WarningType.ORACLE_PRICE_DERIVATION) {
    return ORACLE_PRICE_DERIVATION_VAULT_MESSAGE
  }

  if (warning.type === VaultV2WarningType.ORACLE_UNUSABLE) {
    return ORACLE_UNUSABLE_VAULT_MESSAGE
  }

  if (warning.type === VaultV2WarningType.BAD_DEBT_UNREALIZED) {
    return BAD_DEBT_UNREALIZED_VAULT_MESSAGE
  }

  // `deposit_disabled` is vault state, not a risk warning, so it produces no warning
  // message. It is surfaced via the disabled deposit button + tooltip instead.

  if (
    warning.type === VaultV2WarningType.CUSTOM &&
    'metadata' in warning &&
    getMetadataContent(warning.metadata)
  ) {
    return getMetadataContent(warning.metadata)
  }
}

// Contextual warning helpers - for displaying warnings at specific UI locations

const ORACLE_MARKET_WARNINGS = [
  { type: MarketWarningType.ORACLE_UNUSABLE, message: ORACLE_UNUSABLE_MARKET_MESSAGE },
  {
    type: MarketWarningType.ORACLE_PRICE_DERIVATION,
    message: ORACLE_PRICE_DERIVATION_MARKET_MESSAGE
  }
] as const

/**
 * {@link getOracleMarketWarnings} for an oRPC procedure's warnings, which carry the bridge's
 * lowercase levels. A level the app does not render drops the warning.
 */
export function getOracleProcedureWarnings(
  warnings: readonly { type: string; level: string }[]
): Warning[] {
  return ORACLE_MARKET_WARNINGS.flatMap(({ type, message }) => {
    const warning = warnings.find(w => w.type === type)
    if (!warning || (warning.level !== 'red' && warning.level !== 'yellow')) return []
    return [{ message, level: apiWarningLevelToEnum(warning.level) }]
  })
}
